import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';
import { getTranslatedActivity, centerActivitiesData } from '../translations/activityTranslations';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import markerIcon from "../pics/markerIcon.png";

import centerImage1 from '../pics/แบ่งต่อ1.jpg';
import centerImage2 from '../pics/แบ่งต่อ2.jpg';
import centerImage3 from '../pics/แบ่งต่อ3.jpg';
import centerImage4 from '../pics/แบ่งต่อ4.jpg';
import centerImage5 from '../pics/แบ่งต่อ14.jpg';
import centerImage6 from '../pics/แบ่งต่อ6.jpg';
import centerImage7 from '../pics/แบ่งต่อ14.jpg';
import centerImage8 from '../pics/แบ่งต่อ14.jpg';
import centerImage9 from '../pics/แบ่งต่อ9.jpg';
import centerImage10 from '../pics/แบ่งต่อ10.jpg';
import centerImage11 from '../pics/แบ่งต่อ11.jpg';
import centerImage12 from '../pics/แบ่งต่อ12.jpg';
import centerImage13 from '../pics/แบ่งต่อ13.jpg';

const defaultCenter = { lat: 13.736717, lng: 100.523186 };

function ActivityMapNew() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [featuredActivities, setFeaturedActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [recentDonors, setRecentDonors] = useState([]);

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "activities"));
      const activitiesData = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        const safeString = (value) => {
          if (!value) return '';
          if (typeof value === 'string') return value;
          if (typeof value === 'object') return JSON.stringify(value);
          return String(value);
        };

        const converted = {
          id: doc.id,
          name: safeString(data.name),
          nameEn: safeString(data.nameEn),
          description: safeString(data.description),
          descriptionEn: safeString(data.descriptionEn),
          date: data.date || '',
          location: safeString(data.location),
          locationEn: safeString(data.locationEn),
          locationName: safeString(data.locationName),
          imgURL: safeString(data.imgURL),
          category: safeString(data.category),
          featuredOnHome: data.featuredOnHome || false,
          createdAt: data.createdAt || null,
          updatedAt: data.updatedAt || null,
          latitude: (data.latitude?._lat !== undefined) ? Number(data.latitude._lat) : (typeof data.latitude === 'number' ? Number(data.latitude) : 0),
          longitude: (data.longitude?._long !== undefined) ? Number(data.longitude._long) : (typeof data.longitude === 'number' ? Number(data.longitude) : 0)
        };

        return converted;
      });

      setActivities(activitiesData);
      
      // เลือกกิจกรรมที่จะแสดงในหน้าแรก (เอา 6-12 กิจกรรมที่อัปโหลดล่าสุดที่มีพิกัด)
      const featured = activitiesData
        .filter(act => act.latitude && act.longitude) // กรองเฉพาะที่มีพิกัด
        .sort((a, b) => {
          // เรียงตามวันที่อัปโหลด (createdAt) ถ้าไม่มีใช้ document ID แทน
          if (a.createdAt && b.createdAt) {
            const timeA = a.createdAt.seconds || new Date(a.createdAt).getTime() / 1000;
            const timeB = b.createdAt.seconds || new Date(b.createdAt).getTime() / 1000;
            return timeB - timeA;
          }
          // ถ้าไม่มี createdAt ใช้ document ID เรียงแทน (ID ใหม่จะมาก่อน)
          return b.id.localeCompare(a.id);
        })
        .slice(0, 12); // เอา 12 กิจกรรมล่าสุด
      
      setFeaturedActivities(featured);
      setLoading(false);
    };

    fetchActivities();
  }, []);

  useEffect(() => {
    const fetchDonors = async () => {
      try {
        const q = query(
          collection(db, "donations"),
          where("status", "==", "approved"),
          orderBy("createdAt", "desc"),
          limit(6)
        );
        
        const snapshot = await getDocs(q);
        const donorsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setRecentDonors(donorsData);
      } catch (error) {
        console.error("Error fetching donors:", error);
      }
    };

    fetchDonors();
  }, []);

  const parseDate = (date) => {
    if (!date) return i18n.language === 'en' ? "Unknown date" : "ไม่ทราบวันที่";
    if (date && date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      return isNaN(parsedDate) ? (i18n.language === 'en' ? "Unknown date" : "ไม่ทราบวันที่") : parsedDate.toLocaleDateString();
    }
    return i18n.language === 'en' ? "Unknown date" : "ไม่ทราบวันที่";
  };

  const customIcon = new L.Icon({
    iconUrl: markerIcon,
    iconSize: [15, 20],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });

  const filteredActivities = activities.filter(activity => {
    const translated = getTranslatedActivity(activity, i18n.language);
    const name = translated.name;
    const description = translated.description;
    const location = translated.location;

    const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (location && location.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesSearch;
  });

  const handleSearch = (e) => {
    e.preventDefault();
    // Search functionality already handled by filteredActivities
  };

  return (
    <div style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Floating Hearts Background */}
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: 'fixed',
            left: `${Math.random() * 100}%`,
            bottom: '-50px',
            fontSize: `${1 + Math.random() * 2}rem`,
            opacity: 0.15,
            zIndex: 0,
            pointerEvents: 'none'
          }}
          animate={{
            y: [0, -window.innerHeight - 100],
            x: [0, (Math.random() - 0.5) * 100],
            rotate: [0, 360],
            opacity: [0, 0.15, 0.15, 0]
          }}
          transition={{
            duration: 10 + Math.random() * 10,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: 'linear'
          }}
        >
          {['💖', '💝', '❤️', '💗', '🌟'][Math.floor(Math.random() * 5)]}
        </motion.div>
      ))}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content container-modern">
          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {i18n.language === 'en' ? (
              <>Visit The Most <span style={{color: '#60a5fa'}}>Beautiful Activities</span> In Thailand</>
            ) : (
              <>เยี่ยมชม<span style={{color: '#60a5fa'}}>กิจกรรมที่สวยงาม</span>ที่สุดในประเทศไทย</>
            )}
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {i18n.language === 'en'
              ? 'Make And Join Our Activities. Expert Advice, Tips, Destination Information, And Inspiration From Us.'
              : 'สร้างและเข้าร่วมกิจกรรมของเรา คำแนะนำจากผู้เชี่ยวชาญ เคล็ดลับ ข้อมูลสถานที่ และแรงบันดาลใจจากเรา'
            }
          </motion.p>

          <motion.div
            className="hero-search-box"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <form onSubmit={handleSearch} className="search-input-group">
              <input
                type="text"
                className="search-input"
                placeholder={i18n.language === 'en' ? 'Search activities...' : 'ค้นหากิจกรรม...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button type="submit" className="search-btn">
                <span>🔍</span>
                {i18n.language === 'en' ? 'Search' : 'ค้นหา'}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Recent Donors Section */}
      {recentDonors.length > 0 && (
        <section style={{ 
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          padding: '4rem 0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Animated decorative elements */}
          <motion.div
            style={{
              position: 'absolute',
              top: '20%',
              right: '10%',
              fontSize: '8rem',
              opacity: '0.12'
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 10, -10, 0]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >💝</motion.div>
          <motion.div
            style={{
              position: 'absolute',
              bottom: '15%',
              left: '5%',
              fontSize: '6rem',
              opacity: '0.12'
            }}
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, -5, 5, 0]
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >🙏</motion.div>
          <motion.div
            style={{
              position: 'absolute',
              top: '50%',
              left: '15%',
              fontSize: '5rem',
              opacity: '0.1'
            }}
            animate={{
              y: [0, -15, 0],
              x: [0, 10, 0]
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >✨</motion.div>

          <div className="container-modern" style={{ position: 'relative' }}>
            <motion.div
              className="text-center"
              style={{ marginBottom: '3rem' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                style={{ fontSize: '4rem', marginBottom: '1rem' }}
                animate={{ 
                  rotate: [0, 5, -5, 0],
                  scale: [1, 1.15, 1]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  repeatDelay: 2
                }}
              >
                🎁
              </motion.div>
              <h2 style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '800',
                color: 'white',
                marginBottom: '0.75rem',
                textShadow: '0 2px 20px rgba(0,0,0,0.2)'
              }}>
                {i18n.language === 'en' ? 'Our Generous Donors' : 'ผู้บริจาคผู้มีจิตศรัทธา'}
              </h2>
              <p style={{
                fontSize: '1.2rem',
                color: 'rgba(255,255,255,0.95)',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: '1.7'
              }}>
                {i18n.language === 'en'
                  ? 'Thank you for making our mission possible through your generosity'
                  : 'ขอบคุณที่ทำให้พันธกิจของเราเป็นจริงด้วยน้ำใจอันงดงาม'}
              </p>
            </motion.div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 250px), 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              {recentDonors.map((donor, index) => {
                const avatarColors = [
                  'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
                  'linear-gradient(135deg, #03a9f4 0%, #0288d1 100%)',
                  'linear-gradient(135deg, #00bcd4 0%, #0097a7 100%)',
                  'linear-gradient(135deg, #4dd0e1 0%, #26c6da 100%)',
                  'linear-gradient(135deg, #81d4fa 0%, #4fc3f7 100%)',
                  'linear-gradient(135deg, #b3e5fc 0%, #81d4fa 100%)',
                ];
                const avatarBg = avatarColors[index % avatarColors.length];

                return (
                  <motion.div
                    key={donor.id}
                    style={{
                      background: 'white',
                      borderRadius: '20px',
                      padding: '1.75rem',
                      textAlign: 'center',
                      boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)',
                      border: 'none'
                    }}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -5, scale: 1.02 }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: '60px',
                      height: '60px',
                      borderRadius: '50%',
                      background: avatarBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem',
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: 'white',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      border: '3px solid white'
                    }}>
                      💖
                    </div>

                    {/* Name */}
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: '600',
                      color: '#1a202c',
                      marginBottom: '0.5rem'
                    }}>
                      {i18n.language === 'en' ? `Donor #${index + 1}` : `ผู้บริจาคท่านที่ ${index + 1}`}
                    </h4>

                    {/* Amount or Type */}
                    {donor.amount ? (
                      <div style={{
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        marginBottom: '0.5rem'
                      }}>
                        ฿{donor.amount?.toLocaleString('th-TH')}
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '0.9rem',
                        color: '#64748b',
                        marginBottom: '0.5rem'
                      }}>
                        📦 {i18n.language === 'en' ? 'Items donated' : 'บริจาคสิ่งของ'}
                      </div>
                    )}

                    {/* Heart */}
                    <div style={{ fontSize: '1.25rem' }}>💝</div>
                  </motion.div>
                );
              })}
            </div>

            {/* View All Button */}
            <div className="text-center">
              <motion.button
                onClick={() => navigate('/donors')}
                style={{
                  padding: '1rem 2.5rem',
                  background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
                  color: '#00695c',
                  border: 'none',
                  borderRadius: '16px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 10px 25px rgba(79,195,247,0.3)',
                  transition: 'all 0.3s'
                }}
                whileHover={{ scale: 1.05, y: -3, boxShadow: '0 15px 30px rgba(79,195,247,0.4)' }}
                whileTap={{ scale: 0.98 }}
              >
                {i18n.language === 'en' ? '💙 View All Donors' : '💙 ดูรายชื่อผู้บริจาคทั้งหมด'}
              </motion.button>
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="categories-section"  >
        <div className="container-modern">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {i18n.language === 'en' ? 'Latest Activities' : 'กิจกรรมล่าสุด'}
          </motion.h2>
          <motion.p
            className="section-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {i18n.language === 'en'
              ? 'Discover our most recent activities and initiatives'
              : 'สำรวจกิจกรรมของเราแบ่งตามหมวดหมู่'
            }
          </motion.p>

          <div className="categories-grid">
            {loading ? (
              <div className="text-center py-5 w-100">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              featuredActivities.slice(0, 6).map((activity, index) => {
                const centerImages = [centerImage1, centerImage2, centerImage3, centerImage4, centerImage5, centerImage6, 
                                     centerImage7, centerImage8, centerImage9, centerImage10, centerImage11, centerImage12];
                const translated = getTranslatedActivity(activity, i18n.language);
                const imageUrl = activity.imgURL || centerImages[index % centerImages.length];
                
                return (
                  <motion.div
                    key={activity.id}
                    className="category-card"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/activity/${encodeURIComponent(activity.name)}`, { state: { activities: [activity] } })}
                  >
                    <div className="category-image-wrapper">
                      <img src={imageUrl} alt={translated.name} />
                    </div>
                    <div className="category-name">
                      {translated.name}
                    </div>
                    {activity.date && (
                      <div className="category-date" style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                        📅 {parseDate(activity.date)}
                      </div>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* Activities Map Section */}
      <section className="mt-5" style={{ background: 'var(--gray-50)', padding: '4rem 0' }}>
        <div className="container-modern">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {i18n.language === 'en' ? 'All Activities on Map' : 'กิจกรรมทั้งหมดบนแผนที่'}
          </motion.h2>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">{i18n.language === 'en' ? 'Loading...' : 'กำลังโหลด...'}</p>
            </div>
          ) : (
            <>
              {searchTerm && (
                <div className="text-center text-muted mb-3">
                  <small>
                    {i18n.language === 'en'
                      ? `Found ${filteredActivities.length} activities out of ${activities.length} total`
                      : `พบ ${filteredActivities.length} กิจกรรมจากทั้งหมด ${activities.length} กิจกรรม`}
                  </small>
                </div>
              )}

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                style={{
                  borderRadius: 'var(--radius-2xl)',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-xl)',
                  height: 'clamp(350px, 50vh, 600px)'
                }}
              >
                <MapContainer center={defaultCenter} zoom={10} style={{ width: "100%", height: "100%" }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {filteredActivities.map((act, index) => {
                    let latitude, longitude;

                    if (act.latitude && typeof act.latitude === 'object' && '_lat' in act.latitude) {
                      latitude = act.latitude._lat;
                      longitude = act.longitude._long;
                    } else {
                      latitude = parseFloat(act.latitude);
                      longitude = parseFloat(act.longitude);
                    }

                    if (isNaN(latitude) || isNaN(longitude)) {
                      return null;
                    }

                    return (
                      <Marker
                        key={index}
                        position={{ lat: latitude, lng: longitude }}
                        icon={customIcon}
                        eventHandlers={{
                          click: () => setSelectedActivity(act),
                        }}
                      >
                        {selectedActivity && selectedActivity.name === act.name && (() => {
                          const translated = getTranslatedActivity(act, i18n.language);
                          return (
                            <Popup>
                              <div>
                                <h3>{translated.name || (i18n.language === 'en' ? 'No name' : 'ไม่มีชื่อ')}</h3>
                                <p>{translated.description || (i18n.language === 'en' ? 'No details' : 'ไม่มีรายละเอียด')}</p>
                                <p>
                                  <strong>{i18n.language === 'en' ? 'Date:' : 'วันที่:'}</strong> {parseDate(act.date)}
                                </p>
                                <p>
                                  <strong>{i18n.language === 'en' ? 'Location:' : 'ที่อยู่:'}</strong> {translated.location || (i18n.language === 'en' ? 'Not specified' : 'ไม่ระบุ')}
                                </p>
                                {act.imgURL && (
                                  <img
                                    src={act.imgURL}
                                    alt={translated.name}
                                    style={{
                                      width: "150px",
                                      height: "100px",
                                      objectFit: "cover",
                                      borderRadius: "8px",
                                    }}
                                  />
                                )}
                              </div>
                            </Popup>
                          );
                        })()}
                      </Marker>
                    );
                  })}
                </MapContainer>
              </motion.div>
            </>
          )}
        </div>
      </section>

      {/* Popular Destinations / Center Activities */}
      <section className="categories-section" style={{ background: 'var(--white)' }}>
        <div className="container-modern">
          <motion.h2
            className="section-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {i18n.language === 'en' ? 'Our Activities' : 'กิจกรรมของศูนย์แบ่งต่อ'}
          </motion.h2>

          <div className="activities-grid">
            {centerActivitiesData.map((activity, index) => {
              const centerImages = [centerImage1, centerImage2, centerImage3, centerImage4, centerImage5, centerImage6, centerImage7, centerImage8, centerImage9, centerImage10, centerImage11, centerImage12, centerImage13];
              return (
                <motion.div
                  key={activity.id}
                  className="activity-card-modern"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="activity-image-wrapper">
                    <img src={centerImages[index]} alt={i18n.language === 'en' ? activity.titleEn : activity.titleTh} />
                  </div>
                  <div className="activity-card-body">
                    <h3 className="activity-title">
                      {i18n.language === 'en' ? activity.titleEn : activity.titleTh}
                    </h3>
                    {activity.descriptionTh && (
                      <p className="activity-description">
                        {i18n.language === 'en' ? activity.descriptionEn : activity.descriptionTh}
                      </p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

export default ActivityMapNew;
